import {
	CodeBlock,
	Alert,
	Badge,
	Table,
	Card,
	CardGrid,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Overview() {
	const gamesData = [
		{
			game: 'Dice',
			type: 'Classic',
			houseEdge: '1%',
			features: 'Configurable target, over/under betting, instant results',
		},
		{
			game: 'Plinko',
			type: 'Classic',
			houseEdge: '1%',
			features: '8-16 rows, 3 risk levels, animated ball drop',
		},
		{
			game: 'Coin Flip',
			type: 'Classic',
			houseEdge: '1%',
			features: 'Heads/tails prediction, 3D coin animation',
		},
		{
			game: 'Crash',
			type: 'Multiplayer',
			houseEdge: '1%',
			features: 'Real-time multiplier, auto cash-out, multiplayer lobbies',
		},
	];

	const gamesColumns = [
		{
			key: 'game',
			header: 'Game',
			render: (value: unknown) => <strong>{value as string}</strong>,
		},
		{
			key: 'type',
			header: 'Type',
			render: (value: unknown) => (
				<Badge variant={value === 'Multiplayer' ? 'new' : 'optional'}>
					{value as string}
				</Badge>
			),
		},
		{ key: 'houseEdge', header: 'House Edge' },
		{ key: 'features', header: 'Features' },
	];

	return (
		<div className={styles.docsPage}>
			<h1>Ringfort Studio API</h1>
			<p className={styles.lead}>
				Ringfort Studio is a B2B crypto game platform that enables operators to
				integrate provably fair casino games into their platforms. Authenticate
				your players, generate game URLs, and handle wallet transactions
				seamlessly.
			</p>

			<Alert variant="info" title="Base URL">
				All API requests should be made to:{' '}
				<code>https://api.rngfort.com</code>
			</Alert>

			<h2>Key Features</h2>

			<div className={styles.featureList}>
				<div className={styles.featureItem}>
					<svg
						className={styles.featureIcon}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
					</svg>
					<div className={styles.featureText}>
						<strong>Provably Fair</strong> - HMAC-SHA256 based RNG with
						verifiable outcomes
					</div>
				</div>

				<div className={styles.featureItem}>
					<svg
						className={styles.featureIcon}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
						<line x1="1" y1="10" x2="23" y2="10" />
					</svg>
					<div className={styles.featureText}>
						<strong>Flexible Wallet</strong> - Seamless callbacks or integrated
						platform wallet
					</div>
				</div>

				<div className={styles.featureItem}>
					<svg
						className={styles.featureIcon}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<polygon points="12 2 2 7 12 12 22 7 12 2" />
						<polyline points="2 17 12 22 22 17" />
						<polyline points="2 12 12 17 22 12" />
					</svg>
					<div className={styles.featureText}>
						<strong>Multiple Games</strong> - Dice, Plinko, Coin Flip, Crash,
						and more
					</div>
				</div>

				<div className={styles.featureItem}>
					<svg
						className={styles.featureIcon}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<circle cx="12" cy="12" r="10" />
						<polyline points="12 6 12 12 16 14" />
					</svg>
					<div className={styles.featureText}>
						<strong>Real-time Multiplayer</strong> - WebSocket-powered games
						like Crash
					</div>
				</div>

				<div className={styles.featureItem}>
					<svg
						className={styles.featureIcon}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
					</svg>
					<div className={styles.featureText}>
						<strong>Sandbox Environment</strong> - Test with unlimited balance
						before going live
					</div>
				</div>

				<div className={styles.featureItem}>
					<svg
						className={styles.featureIcon}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
						<polyline points="14 2 14 8 20 8" />
						<line x1="16" y1="13" x2="8" y2="13" />
						<line x1="16" y1="17" x2="8" y2="17" />
					</svg>
					<div className={styles.featureText}>
						<strong>Comprehensive Docs</strong> - Full API reference with code
						examples
					</div>
				</div>
			</div>

			<h2>Quick Example</h2>

			<p>
				Create a game session for your player with a single API call. The
				response includes a URL that loads the game client with the player
				already authenticated.
			</p>

			<CodeBlock language="bash" title="Create a player session">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/sessions \\
  -H "Authorization: Bearer sk_test_demo" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "player_123",
    "currency": "USD",
    "walletMode": "integrated",
    "returnUrl": "https://your-site.com/games"
  }'`}
			</CodeBlock>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "sessionId": "sess_abc123xyz",
    "sessionToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
    "gameUrl": "https://play.rngfort.com/play/dice?token=xxx",
    "expiresAt": 1704067200
  }
}`}
			</CodeBlock>

			<h2>Available Games</h2>

			<p>
				All games support provably fair verification and can be configured with
				custom house edges and betting limits per operator.
			</p>

			<Table columns={gamesColumns} data={gamesData} />

			<h2>Environments</h2>

			<p>
				Ringfort provides two environments for development and production use.
				Use sandbox credentials for testing, then switch to live keys when
				you're ready to accept real bets.
			</p>

			<div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
				<div className={`${styles.envCard} ${styles.sandbox}`}>
					<div className={styles.envCardHeader}>
						<h3 className={styles.envCardTitle}>Sandbox</h3>
						<Badge variant="sandbox">Testing</Badge>
					</div>
					<p className={styles.envCardDescription}>
						Use for development and testing. Unlimited virtual balance, no real
						transactions.
					</p>
					<div className={styles.envCardDetails}>
						<div className={styles.envDetail}>
							<span className={styles.envDetailLabel}>Public Key</span>
							<span className={styles.envDetailValue}>pk_test_*</span>
						</div>
						<div className={styles.envDetail}>
							<span className={styles.envDetailLabel}>Secret Key</span>
							<span className={styles.envDetailValue}>sk_test_*</span>
						</div>
					</div>
				</div>

				<div className={`${styles.envCard} ${styles.live}`}>
					<div className={styles.envCardHeader}>
						<h3 className={styles.envCardTitle}>Live</h3>
						<Badge variant="live">Production</Badge>
					</div>
					<p className={styles.envCardDescription}>
						Use for production. Real wallet integration, actual transactions
						processed.
					</p>
					<div className={styles.envCardDetails}>
						<div className={styles.envDetail}>
							<span className={styles.envDetailLabel}>Public Key</span>
							<span className={styles.envDetailValue}>pk_live_*</span>
						</div>
						<div className={styles.envDetail}>
							<span className={styles.envDetailLabel}>Secret Key</span>
							<span className={styles.envDetailValue}>sk_live_*</span>
						</div>
					</div>
				</div>
			</div>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>

				<p>
					Ready to get started? Follow our quickstart guide to make your first
					API call in under 5 minutes.
				</p>

				<CardGrid columns={2}>
					<Card
						title="Quickstart Guide"
						description="Create your first game session and place a test bet in 5 minutes."
						href="/docs/quickstart"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<polygon points="5 3 19 12 5 21 5 3" />
							</svg>
						}
					/>

					<Card
						title="Authentication"
						description="Learn how to authenticate API requests with your operator keys."
						href="/docs/authentication"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
								<path d="M7 11V7a5 5 0 0 1 10 0v4" />
							</svg>
						}
					/>

					<Card
						title="Environments"
						description="Understand the differences between sandbox and live environments."
						href="/docs/environments"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="2" y1="12" x2="22" y2="12" />
								<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
							</svg>
						}
					/>

					<Card
						title="Integration Guide"
						description="Complete guide to integrating Ringfort games into your platform."
						href="/docs/integration-guide"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<polyline points="16 18 22 12 16 6" />
								<polyline points="8 6 2 12 8 18" />
							</svg>
						}
					/>
				</CardGrid>
			</div>
		</div>
	);
}
